import type { Color } from 'pdf-lib';
import { rgb } from 'pdf-lib';

export function normalizeHex(color?: string) {
  return color ? (color.startsWith('#') ? color : `#${color}`) : undefined;
}

export function cleanHex(color: string | undefined) {
  if (!color) return undefined;
  return color.replace('#', '');
}

// Simple hash function to turn string into a number
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Generate SVG avatar with blur
function generateAvatar(userData, size = 100, blurAmount = 10) {
  const seed = hashCode(userData);

  // Derive two hues from seed (0-359 degrees)
  const hue1 = seed % 360;
  const hue2 = (seed >> 10) % 360; // Shift bits for variation

  // Create colors (adjust saturation/lightness as needed)
  const color1 = `hsl(${hue1}, 70%, 50%)`;
  const color2 = `hsl(${hue2}, 70%, 40%)`;

  // SVG with radial gradient and blur filter
  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="grad" cx="50%" cy="50%" r="100%" fx="50%" fy="50%">
          <stop offset="0%" stop-color="${color1}" />
          <stop offset="100%" stop-color="${color2}" />
        </radialGradient>
        <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="${blurAmount}" />
        </filter>
      </defs>
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="url(#grad)" filter="url(#blur)" />
    </svg>
  `;
}
//
// // Example usage
// const userData = "0x1d0034134e..."; // Your unique data
// const avatarSVG = generateAvatar(userData, 100, 15); // Adjust blurAmount as needed
// document.body.innerHTML = avatarSVG; // Or set as innerHTML of an element

export function generateHslColor(str: string, brightness = 50) {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 60%, ${brightness}%)`;
}

const VIBRANT_HUE_RANGES: [number, number][] = [
  [0, 20],
  [90, 180],
  [200, 280],
  [300, 360],
];

function djb2Hash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i);
  }
  return hash;
}

function hashToVibrantHue(hash: number): number {
  const total = VIBRANT_HUE_RANGES.reduce((sum, [s, e]) => sum + (e - s + 1), 0);
  let idx = Math.abs(hash) % total;
  for (const [start, end] of VIBRANT_HUE_RANGES) {
    const len = end - start + 1;
    if (idx < len) return start + idx;
    idx -= len;
  }
  return 0;
}

export function generateAvatarGradientColors(firstName: string, lastName: string): [string, string] {
  const combined = `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim();
  const hash = djb2Hash(combined || ' ');
  const hue = combined ? hashToVibrantHue(hash) : 200;
  return [`hsl(${hue}, 65%, 72%)`, `hsl(${hue}, 65%, 55%)`];
}
